import { Palette } from '@mui/material';
import type { TypographyOptions } from '@mui/material/styles/createTypography';
import type {} from '@mui/material/Typography';

import { capitalDesignTokens } from './capital_design_tokens';

// tslint:disable:interface-name
declare module '@mui/material/styles' {
  interface TypographyVariants {
    d1: React.CSSProperties;
    d2: React.CSSProperties;
    d3: React.CSSProperties;
    d4: React.CSSProperties;
    d5: React.CSSProperties;
    d6: React.CSSProperties;
    bodyXLarge?: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyDefault: React.CSSProperties;
    bodySmall: React.CSSProperties;
    bodyXSmall: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    d1?: React.CSSProperties;
    d2?: React.CSSProperties;
    d3?: React.CSSProperties;
    d4?: React.CSSProperties;
    d5?: React.CSSProperties;
    d6?: React.CSSProperties;
    bodyXLarge?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyDefault?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
    bodyXSmall?: React.CSSProperties;
  }
}

declare module '@mui/material/styles/createTypography' {
  interface Typography {
    d1: React.CSSProperties;
    d2: React.CSSProperties;
    d3: React.CSSProperties;
    d4: React.CSSProperties;
    d5: React.CSSProperties;
    d6: React.CSSProperties;
    bodyXLarge?: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyDefault: React.CSSProperties;
    bodySmall: React.CSSProperties;
    bodyXSmall: React.CSSProperties;
    fontWeightSemiBold: number;
  }
  interface TypographyOptions {
    d1?: React.CSSProperties;
    d2?: React.CSSProperties;
    d3?: React.CSSProperties;
    d4?: React.CSSProperties;
    d5?: React.CSSProperties;
    d6?: React.CSSProperties;
    bodyXLarge?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyDefault?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
    bodyXSmall?: React.CSSProperties;
    fontWeightSemiBold?: number;
  }
}

type TypographyType = typeof capitalDesignTokens.foundations.typography;
type FontSizeKey = keyof TypographyType['fontSize'];
type FontWeightKey = keyof TypographyType['fontWeight'];

const typographyConfig = capitalDesignTokens.foundations.typography;

export const getFontStyles = (fontSizeKey: FontSizeKey, fontWeight: FontWeightKey = 'normal') => {
  return {
    fontSize: typographyConfig.fontSize[fontSizeKey],
    lineHeight: typographyConfig.lineHeight[fontSizeKey],
    fontWeight: typographyConfig.fontWeight[fontWeight],
    fontFamily: typographyConfig.baseFont,
    fontFeatureSettings: "'ss03' on"
  };
};

export const typography: TypographyOptions = {
  fontFamily: typographyConfig.baseFont,
  h1: getFontStyles('h1', 'semiBold'),
  h2: getFontStyles('h2', 'semiBold'),
  h3: getFontStyles('h3', 'semiBold'),
  h4: getFontStyles('h4', 'semiBold'),
  h5: getFontStyles('h5', 'semiBold'),
  h6: getFontStyles('h6', 'semiBold'),
  d1: getFontStyles('displayXLarge', 'light'),
  d2: getFontStyles('displayLarge', 'light'),
  d3: getFontStyles('displayMedium', 'light'),
  d4: getFontStyles('displaySmall', 'light'),
  d5: getFontStyles('displayXSmall', 'light'),
  d6: getFontStyles('displayXXSmall', 'light'),
  body1: getFontStyles('bodyDefault'),
  body2: getFontStyles('bodySmall'),
  button: { ...getFontStyles('bodyDefault', 'semiBold'), textTransform: 'none' },
  overline: getFontStyles('bodySmall', 'medium'),
  bodyXLarge: getFontStyles('bodyXLarge'),
  bodyLarge: getFontStyles('bodyLarge'),
  bodyDefault: getFontStyles('bodyDefault'),
  bodySmall: getFontStyles('bodySmall'),
  bodyXSmall: getFontStyles('bodyXSmall'),
  fontWeightSemiBold: typographyConfig.fontWeight.semiBold
};

// overline is the only variant with a colour. Taking it from the palette makes it follow dark mode wherever the variant
// is used: <Typography>, sx/Box `typography`, and styled spreads of theme.typography.overline. A color prop or an sx
// colour still wins, as before.
export const getTypography = (palette: Palette): TypographyOptions => ({
  ...typography,
  overline: { ...typography.overline, color: palette.text.secondary }
});
